import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { User, UserAddress, ActivationRequest, PlusiPoints } from '../_models';
import { AuthenticationService } from './authentication.service';
import { map, tap, flatMap, catchError, concatMap } from 'rxjs/operators';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService } from './utils.service';
import { AppPlusi, AppDateValidate, AppCertCreate } from '../_models/appplusi';
import { CuponDescuento, CuponView } from '../_models/coupons';
import { DomSanitizer } from '@angular/platform-browser';
import { UserCouponsComponent } from '../user/user/user-coupons/user-coupons.component';

@Injectable({ providedIn: 'root' })
export class UserService {
    
    public editingUserSubject: BehaviorSubject<User>
    public editingUser : Observable<User>;
    
    constructor(
        private http: HttpClient,
        private auth : AuthenticationService,
        private toastr : ToastrService,
        private translate : TranslateService,
        private utils : UtilsService,
        private dom : DomSanitizer

     ) { 
        this.auth.currentUser.subscribe(user => {
            this.editingUserSubject = new BehaviorSubject<User>(user);
            this.editingUser = this.editingUserSubject.asObservable();
        });
     }


    get(user: User){
        return this.http.get<User>(`${environment.API_ENDPOINT}users/${user.id_user}/show`).pipe(
            map (result => {
                if (user.user_addresses){
                    user.user_addresses = user.user_addresses.map( address => new UserAddress(address));
                }
                result.user_addresses.forEach(address => {
                    address.mapURL = this.dom.bypassSecurityTrustResourceUrl(`https://maps.google.com/maps?q=${address.addressing}&t=&z=17&ie=UTF8&iwloc=&output=embed`);
                });
                return result;
            })
        );
    }

    create(user : User) {
        return this.http.post<User>(`${environment.API_ENDPOINT}users/${user.id_user}/create`,user).pipe(
            tap (data => {
                    this.toastr.success(this.translate.instant('NEWUSER.SUCCESS'));
                },error => {
                    this.toastr.error(this.translate.instant('NEWUSER.ERROR'));
                })
            );
    }

    update(user : User) {
        return this.http.put<User>(`${environment.API_ENDPOINT}users/${this.auth.currentUserValue.id_user}/update`,user).pipe(
            concatMap ( next => {
                this.toastr.success(this.translate.instant('EDITUSER.SUCCESS'));
                 return this.refresh();
            }),
            catchError( error =>{
                this.toastr.error(this.translate.instant('EDITUSER.ERROR'));
                return this.refresh();
            })
        );
    }

    
    requestCard(user : User) {
        return this.http.put<User>(`${environment.API_ENDPOINT}users/${this.auth.currentUserValue.id_user}/update`,user).pipe(
            tap (next => {
                this.toastr.success(this.translate.instant('USERCARD.SUCCESS_REQUEST'));
                this.refresh();
            }, 
            error =>{
                this.toastr.error(this.translate.instant('USERCARD.ERROR_REQUEST'));
                this.refresh();
            })
        );
    }

    activate(request: ActivationRequest) {
        return this.http.put<string>(`${environment.API_ENDPOINT}users/${request.id_user}/activate`,request).pipe(
            tap (result =>{
                    switch (parseInt(result)){
                        case 1: 
                            this.toastr.success(this.translate.instant('USERVALIDATE.VALIDATED'));
                            break;
                        default :
                            this.toastr.error(this.translate.instant('USERVALIDATE.ERROR'));    
                    }
                },error =>{
                    this.toastr.error(this.translate.instant('USERVALIDATE.ERROR'));
                })
        );
    }

    delete(user : User)  {
        return this.http.delete<string>(`${environment.API_ENDPOINT}users/${user.id_user}/delete`).pipe(
            tap (result =>{
      
                switch (parseInt(result)) {
                  case 1:
                    this.toastr.success(this.translate.instant('UNSUBSCRIBE.SUCCESS'));
                    this.auth.logout().subscribe();
                    break;
                };
              },error => {
                switch (parseInt(error.error)) {
                  case 2:
                  case 3:
                    this.toastr.error(this.translate.instant('UNSUBSCRIBE.ERROR_' + error.error ));
                    break;
                  default:
                    this.toastr.error(this.translate.instant('UNSUBSCRIBE.ERROR'));
                    break; 
                }
              }
            )
        );
    }

    changePassword (user: User) {
        return this.http.put<any>(`${environment.API_ENDPOINT}users/${user.id_user}/password`,
            { id_user : user.id_user.toLocaleLowerCase(), 
              password : user.password,
              passwordtoken : 0,
              guid : '',
            }).pipe(
                tap (result =>{
                        this.toastr.success( this.translate.instant(`CHANGEPASSWORD.SUCCESS`));
                    },error =>{
                        this.toastr.error( this.translate.instant(`CHANGEPASSWORD.ERROR`));
                })
            );
    }

    checkAvailableUserName(id_user: string) {
        return this.http.get<any>(`${environment.API_ENDPOINT}users/${id_user}/check`);
    }

    checkAvailableEmail(email : string) {
        return this.http.get<any>(`${environment.API_ENDPOINT}users/${email}/email`);
    }

    addAddress (address : UserAddress){
        return this.http.post<string>(`${environment.API_ENDPOINT}users/${this.auth.currentUserValue.id_user}/newaddress`,address).pipe(
            concatMap (next => {
                this.toastr.success(this.translate.instant('USERADDRESS.SUCCESS_ADD'));
                // user service updates editingUser, should be new address there       
                return this.refresh();
            }),
            catchError (error =>{
                this.toastr.error(this.translate.instant('USERADDRESS.ERROR_ADD'));
                return this.refresh();
            })
        );
    }
    
    updateAddress (address : UserAddress)  {
        // console.log('users.updateAddress');
        return this.http.put<string>(`${environment.API_ENDPOINT}users/${this.auth.currentUserValue.id_user}/updateaddress`,address).pipe(
            concatMap ( next => {
                this.toastr.success(this.translate.instant('USERADDRESS.SUCCESS_UPDATE'));
                return this.refresh();
            }), 
            catchError ( error => {
                switch(parseInt(error.error)){
                    case 2:
                    case 3:
                    case 4:
                    case 5:
                    case 6:
                    this.toastr.error(this.translate.instant('USERADDRESS.ERROR_UPDATE_' + error.error));
                      break;
                    default:
                      this.toastr.error(this.translate.instant('USERADDRESS.ERROR_UPDATE'));
                      break;
                }
                return this.refresh();
            })
        );
    }

    deleteAddress (address: UserAddress){
        return this.http.delete<string>(`${environment.API_ENDPOINT}users/${this.auth.currentUserValue.id_user}/${address.recid}/deleteaddress`).pipe(
            concatMap (result => {
                this.toastr.success(this.translate.instant('USERADDRESS.SUCCESS_DELETE'));
                return this.refresh();
            }),
            catchError(error => {
                switch( parseInt(error.error)){
                    case 2:
                    case 3:
                    case 4:
                    case 5:
                        this.toastr.error(this.translate.instant('USERADDRESS.ERROR_DELETE_' + error.error ));
                        break;
                    default:
                        this.toastr.error(this.translate.instant('USERADDRESS.ERROR_DELETE'));
                        break;
                }
                return this.refresh();
            })
        );

    }

    private _requestPasswordRecovery (id_user : string, ip : string){
        return this.http.put<string>(`${environment.API_ENDPOINT}users/${id_user}/requestpass`, {
            ipuser : ip
        });
    }

    requestPasswordRecovery (username : string){
        return this.utils.getClientIp().pipe(
            flatMap ( result => {
                return this._requestPasswordRecovery(username, result.ip).pipe(
                    map(result => {
                        switch (parseInt(result)){
                        case 1:
                            this.toastr.success(this.translate.instant('FORGOTPASSWORD.SUCCESS'));     
                            break;
                        default :
                            this.toastr.error(this.translate.instant('FORGOTPASSWORD.CANCEL_ERROR')); 
                        }
                        return result;
                    }), 
                    catchError (error => {
                        // this.toastr.error(this.translate.instant('FORGOTPASSWORD.ERROR')); 
                        return throwError(error);
                    }));
                }),
            catchError( error => {
                this.toastr.error(this.translate.instant('FORGOTPASSWORD.ERROR'));
                return throwError(error);
            })
        );
    }
    
    private _cancelPasswordRecovery (id_user : string, ip : string, guid : string){
        return this.http.put<string>(`${environment.API_ENDPOINT}users/${id_user}/cancelpass`, {
            id_user : id_user,
            ipuser : ip,
            passwordtoken : 1,
            guid : guid
        });
    }

    cancelPasswordRecovery (id_user : string, guid : string){       
        return this.utils.getClientIp().pipe(
            flatMap (result => {
                return this._cancelPasswordRecovery(id_user, result.ip, guid).pipe(
                    map(result => {
                        switch (parseInt(result)){
                            case 1 : 
                                this.toastr.success(this.translate.instant('RESETPASSWORD.CANCEL_SUCCESS')); 
                                break;
                            default :
                                this.toastr.error(this.translate.instant('RESETPASSWORD.CANCEL_ERROR')); 
                        }
                    }),
                    catchError(error =>{
                        // this.toastr.error(this.translate.instant('RESETPASSWORD.CANCEL_ERROR')); 
                        return throwError(error);
                    }));
                }),
                catchError(error => {
                    this.toastr.error(this.translate.instant('RESETPASSWORD.CANCEL_ERROR')); 
                    return throwError(error);
                })
        );       
    }

    recoverPassword (id_user: string, password : string, guid : string){
        return this.http.put<string>(`${environment.API_ENDPOINT}users/${id_user}/recover`, {
            id_user : id_user,
            password : password,
            passwordtoken : 1,
            guid : guid
        }).pipe(
            tap (
                result => {
                    switch (parseInt(result)){
                        case 1:
                            this.toastr.success(this.translate.instant('RESETPASSWORD.SUCCESS'));
                            break;                      
                    }
                }, 
                error =>{
                    this.toastr.error(this.translate.instant('RESETPASSWORD.ERROR')); 
                }
            ));
    }
    
    refresh() {
        return this.auth.refresh().pipe(
            concatMap ( next => {
                this.editingUserSubject.next(next);
                return this.editingUser;
            })
        );
    }

    ///////////////////
    // TARJETA PLUS
    ///////////////////

    // getPoints() : Observable<any> {
    //     return this.http.get<string>(`${environment.API_ENDPOINT}fidelity/${this.auth.currentUserValue.id_user}/punts`).pipe(
    //         map ( result => {
    //             let points : number =  parseInt(result);
    //             if (points < 0){
    //                 throw (result);
    //             }                               
    //             return points / 100;
    //         }),
    //         catchError (error =>{
    //             this.toastr.error(this.translate.instant('USERCARD.ERROR_POINTS'));
    //             return throwError(error);
    //         })
    //     );
    // }

    getPromoPoints() : Observable<PlusiPoints> {
        if (this.auth.currentUserValue.id_plusi_card != ''){
            return this.http.get<PlusiPoints>(`${environment.API_ENDPOINT}users/plusi/${this.auth.currentUserValue.id_plusi_card}/points`);
        }
        return of(new PlusiPoints());
    }

    ///////////////////
    // APP PLUS
    ///////////////////

    /**
     * Comprueba si el DNI pasado (que tiene que ser el del usuario logado) tiene algun código de activación pendiente.
     * En el proceso es el primer paso
     *
     * @param {string} dni
     * @returns {Observable<AppPlusi>}
     * @memberof UserService
     */
    checkAppActivationCode(dni: string) : Observable<AppPlusi> {
        return this.http.get<AppPlusi>(`${environment.API_ENDPOINT}app/${dni}/active`);
    }

    /**
     * Devuelve las fechas de un código de activación para que se pueda comprobar si es válido.
     * Si checkAppActivationCode devuelve un código solicitado, con esta función solicitamos 
     * las fechas clave para comprobar si el código es válido todavía. 
     * 
     * @param {string} codigo
     * @returns
     * @memberof UserService
     */
    checkAppActivationCodeDate(codigo : string) : Observable<AppDateValidate>{
        return this.http.get<AppDateValidate>(`${environment.API_ENDPOINT}app/${codigo}/validate`);
    }

    /**
     * Pide un código de activación par ala App. Hay que comprobar primero que no haya uno anterior válido o pendiente.
     * si checkAppActivationCode devuelve que no hay código solicitado, con esta función se puede solicitar.
     * si checkAppActivationCode devuelve un código y checkAppActivationCodeDate devuelve una fecha ya caducada, también se puede solicitar.
     * 
     * @see checkAppActivationCode
     * @see checkAppActivationCodeDate
     *
     * @param {string} id_user
     * @returns Observable<string>
     * @memberof UserService
     */
    requestAppActivationCode(id_user:string, tarjeta: string, dni_user: string) : Observable <string>{
        return this.http.post<string>(`${environment.API_ENDPOINT}app/${id_user}/activate`, {
            glidcliente: tarjeta,
            dni : dni_user
        });
    }

    ///////////////////
    // COUPONS
    ///////////////////

    getCupones(id_plusi_card:string, lang_id:string)  : Observable<CuponDescuento[]>{
        lang_id += "_ES";
        return this.http.get<CuponDescuento[]>(`${environment.API_ENDPOINT}vouchers/reports/webvouchers/${id_plusi_card}/${lang_id}`).pipe(
            catchError (error => {
                this.toastr.error(this.translate.instant('USERCOUPONS.ERROR_ACTIVATING'));
                return of(error);
            })
        );
    }

    validCupon(CuponCode:string ) {
        return this.http.put<string>(`${environment.API_ENDPOINT}vouchers/movements/createfromcode/${CuponCode}`,null).pipe();
    }



    /**
     * Activa un cupón de descuento
     *
     * @param {string} id_plusi_card
     * @param {CuponView} coupon
     * @returns
     * @memberof UserService
     */
    requestSetCuponActivo(id_plusi_card:string, coupon : CuponView){
        return this.http.post(`${environment.API_ENDPOINT}vouchers/movements/new`, {
            custid : id_plusi_card,
            vouid : coupon.vou_id,
            movtype : 2,
            misc: ""
        });
    }
      
    /**
     *Desactiva un cupón de descuento
     *
     * @param {string} id_plusi_card
     * @param {CuponView} coupon
     * @returns
     * @memberof UserService
     */
    requestSetCuponInactivo(id_plusi_card:string, coupon : CuponView){
        return this.http.delete(`${environment.API_ENDPOINT}vouchers/movements/del/${id_plusi_card}/${coupon.vou_id}/2`);
    }


}
